import { create } from 'zustand';
import { round2 } from './shared';

/** Pricing block as returned by GET /catalog/products/:id/availability. */
export interface PriceInfo {
  unitPriceHT: number;
  finalUnitPriceHT: number;
  unitPriceTTC: number;
  discountPct: number;
  depositHT: number;
}

export interface CartLine {
  /**
   * Our product, when the part is one we hold. For a supplier's offer with no record yet this is
   * the SEARCH ROW's id (`sup:<code>:<number>`) and serves only as the basket key — `offer` below
   * is what travels to the server, and the record is made at checkout rather than while the
   * buyer is still browsing (see services/catalog/offerLines.ts).
   */
  productId: string;
  /** Set when this line is a supplier's offer we have not (yet) minted a record for. */
  offer?: {
    matchedNumber: string;
    supplierArticleNo: string | null;
    name: string;
    brand: string | null;
  };
  sku: string;
  name: string;
  brand: string | null;
  qty: number;
  price: PriceInfo;
  /**
   * The ORIGIN this quantity was composed against, and no longer a hint: the buyer chose it,
   * looking at that origin's availability, price and delivery date. It travels to the server on
   * the order line so the split is honoured rather than re-derived from stock.
   */
  source: Origin;
  /** INV-47 — this supplier line replaces stock that had gone; the buyer agreed on screen. */
  switchAgreed?: boolean;
}

export type Origin = 'INTERNAL' | 'EXTERNAL';
/**
 * A basket entry is keyed by PRODUCT **and** ORIGIN.
 *
 * One reference can be bought from two places at once — some off the shelf now, the rest ordered
 * in for a later date — and those are two different acquisitions at two different prices. Keyed on
 * productId alone they would merge into one line at one averaged price: a figure matching no real
 * purchase, unreadable on the invoice and impossible to credit correctly if one unit comes back.
 */
export const lineKey = (productId: string, source: Origin): string => `${productId}:${source}`;

export interface CartTotals {
  units: number;
  subtotalHT: number;
  depositHT: number;
  totalHT: number;
  vat: number;
  totalTTC: number;
}

interface CartStore {
  lines: CartLine[];
  /** Customer purchase-order / reference, printed on the order or quote. */
  poRef: string;
  add: (line: Omit<CartLine, 'qty'>, qty?: number) => void;
  setQty: (productId: string, source: Origin, qty: number) => void;
  inc: (productId: string, source: Origin, delta: number) => void;
  remove: (productId: string, source: Origin) => void;
  clear: () => void;
  setPoRef: (v: string) => void;
}

export const useCartStore = create<CartStore>((set) => ({
  lines: [],
  poRef: '',

  add(line, qty = 1) {
    set((s) => {
      const idx = s.lines.findIndex(
        (l) => lineKey(l.productId, l.source) === lineKey(line.productId, line.source)
      );
      if (idx >= 0) {
        const next = [...s.lines];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return { lines: next };
      }
      return { lines: [...s.lines, { ...line, qty }] };
    });
  },

  setQty(productId, source, qty) {
    set((s) => ({
      lines: s.lines.map((l) =>
        l.productId === productId && l.source === source ? { ...l, qty: Math.max(1, qty) } : l
      ),
    }));
  },

  inc(productId, source, delta) {
    set((s) => ({
      lines: s.lines.map((l) =>
        l.productId === productId && l.source === source
          ? { ...l, qty: Math.max(1, l.qty + delta) }
          : l
      ),
    }));
  },

  remove(productId, source) {
    set((s) => ({
      lines: s.lines.filter((l) => !(l.productId === productId && l.source === source)),
    }));
  },

  clear() {
    set({ lines: [], poRef: '' });
  },

  setPoRef(v) {
    set({ poRef: v });
  },
}));

/**
 * Display-only totals. The authoritative numbers always come back from the
 * server on POST /orders | /quotes | /pos/ticket — the backend snapshots prices.
 */
export function cartTotals(lines: CartLine[]): CartTotals {
  let subtotalHT = 0;
  let depositHT = 0;
  let vat = 0;
  let units = 0;
  for (const l of lines) {
    subtotalHT += l.price.finalUnitPriceHT * l.qty;
    depositHT += l.price.depositHT * l.qty;
    vat += (l.price.unitPriceTTC - l.price.finalUnitPriceHT) * l.qty;
    units += l.qty;
  }
  const totalHT = round2(subtotalHT + depositHT);
  return {
    units,
    subtotalHT: round2(subtotalHT),
    depositHT: round2(depositHT),
    totalHT,
    vat: round2(vat),
    totalTTC: round2(totalHT + vat),
  };
}
